import { API_CONFIG, buildBackendUrl } from '../config/api-config';
import { ApiClientError } from './types';
/** Time limit for POST / PUT / PATCH / DELETE when the caller sets none (reads keep the default) */
const WRITE_TIMEOUT_MS = 60000;
/** Server error text that is code / DB detail, not a message for users */
const TECHNICAL_ERROR = /prisma|invocation in|node_modules|\.(ts|js):\d+|[A-Z]:\\|violates (foreign key|unique|not-null|check) constraint|Unique constraint failed|Foreign key constraint|relation "[^"]+" does not exist|column "[^"]+" does not exist|syntax error at or near|ECONNREFUSED|ETIMEDOUT|ECONNRESET|socket hang up|Cannot read propert|is not a function|is not defined|Unexpected token/i;
/**
 * Generic API Client with authentication and retry logic
 * Packages construct their own full URLs using this client
 */
export class ApiClient {
    constructor(baseUrl, options = {}) {
        this.baseUrl = baseUrl;
        this.defaultOptions = {
            timeout: API_CONFIG.DEFAULTS.timeout,
            retries: API_CONFIG.DEFAULTS.retries,
            headers: {
                ...API_CONFIG.DEFAULTS.headers,
                ...options.headers
            },
            ...options
        };
    }
    /**
     * Set callback for when 401 Unauthorized response is received
     */
    setUnauthorizedCallback(callback) {
        this.onUnauthorized = callback;
    }
    /**
     * Get authentication headers
     */
    getAuthHeaders() {
        const token = localStorage.getItem(API_CONFIG.AUTH.tokenKey);
        const headers = {};
        if (token) {
            headers[API_CONFIG.AUTH.tokenHeader] = `${API_CONFIG.AUTH.tokenPrefix} ${token}`;
        }
        return headers;
    }
    /**
     * Make HTTP request with retry logic and error handling
     */
    async makeRequest(endpoint, config = {}) {
        const { method = 'GET', headers: configHeaders = {}, body, 
        // Saves get longer than reads: a slow save that still finishes on the server used to be
        // cut off at 10 s and shown as an error (and saved again by a second click)
        timeout = method === 'GET' ? this.defaultOptions.timeout : Math.max(this.defaultOptions.timeout, WRITE_TIMEOUT_MS), retries = this.defaultOptions.retries, signal: externalSignal, } = config;
        const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
        // Check if body is FormData
        const isFormData = body instanceof FormData;
        // Don't set Content-Type for FormData (browser will set it with boundary)
        const finalHeaders = isFormData
            ? {
                ...this.getAuthHeaders(),
                ...configHeaders
            }
            : {
                'Content-Type': 'application/json',
                ...this.getAuthHeaders(),
                ...configHeaders
            };
        let lastError = null;
        // Only reads are retried. A save that timed out may already have been stored on the
        // server, so resending POST / PUT / PATCH / DELETE could create duplicates (PO, check-in…).
        const maxRetries = method === 'GET' ? retries : 0;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), timeout);
                // Wire external signal: abort internal controller when caller cancels
                if (externalSignal) {
                    if (externalSignal.aborted) {
                        controller.abort();
                    }
                    else {
                        externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
                    }
                }
                const response = await fetch(url, {
                    method,
                    headers: finalHeaders,
                    body: isFormData ? body : (body ? JSON.stringify(body) : undefined),
                    signal: controller.signal,
                });
                clearTimeout(timeoutId);
                if (!response.ok) {
                    // Handle 401 — attempt token refresh once, then retry
                    if (response.status === 401 && !endpoint.includes('/auth/logout') && !endpoint.includes('/auth/refresh')) {
                        const newToken = await this._handleUnauthorized();
                        if (newToken) {
                            // Retry once with the new token
                            const retryHeaders = {
                                ...finalHeaders,
                                [API_CONFIG.AUTH.tokenHeader]: `${API_CONFIG.AUTH.tokenPrefix} ${newToken}`,
                            };
                            const retryResponse = await fetch(url, {
                                method,
                                headers: retryHeaders,
                                body: isFormData ? body : (body ? JSON.stringify(body) : undefined),
                            });
                            if (retryResponse.ok) {
                                const ct = retryResponse.headers.get('content-type');
                                if (ct?.includes('application/json'))
                                    return await retryResponse.json();
                                return (await retryResponse.text());
                            }
                        }
                        // Refresh failed or retry also 401 → logout
                        if (this.onUnauthorized)
                            this.onUnauthorized();
                    }
                    let errorMessage = response.statusText;
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData.message || errorData.error || errorMessage;
                    }
                    catch {
                        // Use default error message if JSON parsing fails
                    }
                    // Some handlers send the raw server error (Prisma call, file path, DB constraint text);
                    // users get a plain message instead, the detail stays in the browser console.
                    if (TECHNICAL_ERROR.test(errorMessage)) {
                        console.error(`[API] ${response.status} ${endpoint}:`, errorMessage);
                        errorMessage = 'Something went wrong. Please try again or contact admin.';
                    }
                    throw new ApiClientError(errorMessage, response.status, response);
                }
                // Handle response
                const contentType = response.headers.get('content-type');
                if (contentType?.includes('application/json')) {
                    return await response.json();
                }
                else {
                    return (await response.text());
                }
            }
            catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                // Don't retry on 4xx errors (except 408 timeout)
                if (error instanceof ApiClientError && error.status >= 400 && error.status < 500 && error.status !== 408) {
                    break;
                }
                // TEMPORARILY DISABLED: Don't retry on 5xx errors for easier debugging
                if (error instanceof ApiClientError && error.status >= 500) {
                    break;
                }
                if (attempt < maxRetries && this.isRetryableError(error)) {
                    // Wait before retry (exponential backoff)
                    await this.delay(Math.pow(2, attempt) * 1000);
                    continue;
                }
                break;
            }
        }
        throw lastError;
    }
    /**
     * Check if error is retryable
     */
    isRetryableError(error) {
        if (error instanceof ApiClientError) {
            // Retry on server errors (5xx) and timeout (408)
            return (error.status >= 500 && error.status < 600) || error.status === 408;
        }
        // Retry on network errors, timeout errors, etc.
        return error.name === 'AbortError' || error.name === 'TypeError';
    }
    /**
     * Delay utility for retry logic
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    /**
     * HTTP Methods
     */
    async get(endpoint, config) {
        return this.makeRequest(endpoint, { ...config, method: 'GET' });
    }
    async post(endpoint, body, config) {
        return this.makeRequest(endpoint, { ...config, method: 'POST', body });
    }
    async put(endpoint, body, config) {
        return this.makeRequest(endpoint, { ...config, method: 'PUT', body });
    }
    async delete(endpoint, config) {
        return this.makeRequest(endpoint, { ...config, method: 'DELETE' });
    }
    async multiDelete(endpoint, ids, config) {
        return this.makeRequest(endpoint, { ...config, method: 'DELETE', body: { ids } });
    }
    async patch(endpoint, body, config) {
        return this.makeRequest(endpoint, { ...config, method: 'PATCH', body });
    }
    /**
     * Raw fetch with the same auth as the methods above: current token, one refresh + retry on 401.
     * Returns the Response untouched, for callers that need a blob / stream / custom handling.
     * `url` is used exactly as given (like fetch) — callers already build it with buildBackendUrl,
     * which is relative ('/api/...') when VITE_API_URL is empty, so it must not get the base again.
     */
    async fetchRaw(url, init = {}) {
        const withToken = (token) => {
            const headers = new Headers(init.headers);
            if (token)
                headers.set(API_CONFIG.AUTH.tokenHeader, `${API_CONFIG.AUTH.tokenPrefix} ${token}`);
            return { ...init, headers };
        };
        const response = await fetch(url, withToken(localStorage.getItem(API_CONFIG.AUTH.tokenKey)));
        if (response.status !== 401 || url.includes('/auth/logout') || url.includes('/auth/refresh'))
            return response;
        const newToken = await this._handleUnauthorized();
        if (newToken) {
            const retryResponse = await fetch(url, withToken(newToken));
            if (retryResponse.status !== 401)
                return retryResponse;
        }
        if (this.onUnauthorized)
            this.onUnauthorized();
        return response;
    }
    /**
     * Authentication Methods
     */
    setAuthToken(token) {
        this.defaultOptions.headers = this.defaultOptions.headers || {};
        this.defaultOptions.headers[API_CONFIG.AUTH.tokenHeader] = `${API_CONFIG.AUTH.tokenPrefix} ${token}`;
    }
    clearAuthToken() {
        if (this.defaultOptions.headers) {
            delete this.defaultOptions.headers[API_CONFIG.AUTH.tokenHeader];
        }
    }
    setOnUnauthorized(callback) {
        this.onUnauthorized = callback;
    }
    // ── Token refresh (shared lock across all ApiClient instances) ────────────
    async _handleUnauthorized() {
        // If already refreshing, wait for the same result
        if (ApiClient._isRefreshing && ApiClient._refreshPromise) {
            return ApiClient._refreshPromise;
        }
        ApiClient._isRefreshing = true;
        ApiClient._refreshPromise = this._doRefresh();
        const token = await ApiClient._refreshPromise;
        ApiClient._isRefreshing = false;
        ApiClient._refreshPromise = null;
        if (token)
            ApiClient._lastRefreshedAt = Date.now();
        return token;
    }
    async _doRefresh() {
        try {
            const currentToken = localStorage.getItem(API_CONFIG.AUTH.tokenKey);
            if (!currentToken)
                return null;
            const lastActivity = parseInt(localStorage.getItem('textile_erp_last_activity') || '0', 10);
            const idleMs = lastActivity ? Date.now() - lastActivity : 0;
            const response = await fetch(`${this.baseUrl}/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    [API_CONFIG.AUTH.tokenHeader]: `${API_CONFIG.AUTH.tokenPrefix} ${currentToken}`,
                },
                body: JSON.stringify({ idleMs }),
            });
            if (response.ok) {
                const data = await response.json();
                const newToken = data?.token;
                if (newToken) {
                    localStorage.setItem(API_CONFIG.AUTH.tokenKey, newToken);
                    localStorage.setItem('textile_erp_last_refresh', Date.now().toString());
                    return newToken;
                }
            }
            return null;
        }
        catch {
            return null;
        }
    }
    /** Returns true if a successful refresh happened within the last [ms] milliseconds. */
    static wasRecentlyRefreshed(ms) {
        return ApiClient._lastRefreshedAt > 0 && Date.now() - ApiClient._lastRefreshedAt < ms;
    }
}
// Shared token refresh lock — prevents concurrent refresh calls
ApiClient._isRefreshing = false;
ApiClient._refreshPromise = null;
ApiClient._lastRefreshedAt = 0;
/**
 * Factory function to create API client with base configuration
 */
export const createApiClient = (baseUrl, options) => {
    return new ApiClient(baseUrl, options);
};
let authFetchClient = null;
/**
 * Drop-in for fetch() on backend URLs: adds the logged-in user's token and refreshes it once on 401.
 * Use this instead of reading the token from localStorage and calling fetch() directly.
 */
export const authFetch = (url, init) => {
    authFetchClient ?? (authFetchClient = new ApiClient(buildBackendUrl('/api')));
    return authFetchClient.fetchRaw(url, init);
};
//# sourceMappingURL=client.js.map